/**
 * @fileoverview Provide shared suggestion selection without owning the source data or saved choice.
 *
 * The parent supplies search results and controlled values; this control owns transient search/overlay interaction. Selection and optional free-text creation return through caller callbacks. Unlike `AppSelect.js`, this boundary supports multiple values and asynchronous suggestions. Business validation and persistence remain with the consuming workspace.
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import AppIcon from "./AppIcon";

const wrapperStyle = {
  position: 'relative',
  width: '100%'
};

const containerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.35rem',
  alignItems: 'center',
  padding: '0.35rem 0.5rem',
  borderRadius: '6px',
  border: '1px solid var(--surface-border)',
  backgroundColor: 'var(--surface-section)',
  minHeight: '38px',
  cursor: 'text'
};

const borderlessContainerStyle = {
  ...containerStyle,
  border: 'none',
  borderBottom: '1px solid transparent',
  borderRadius: '0',
  backgroundColor: 'transparent',
  padding: '0.35rem 0'
};

const chipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3rem',
  padding: '0.2rem 0.55rem',
  borderRadius: '999px',
  backgroundColor: 'var(--surface-200, #e2e8f0)',
  color: 'var(--text-color)',
  fontSize: '0.8rem',
  fontWeight: 500
};

const chipRemoveStyle = {
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: '0.7rem',
  color: 'var(--text-color-secondary, #64748b)',
  display: 'inline-flex',
  alignItems: 'center'
};

const inputInternalStyle = {
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: 'var(--text-color)',
  fontSize: '0.875rem',
  flex: 1,
  minWidth: '80px',
  fontFamily: 'inherit',
  padding: '0.15rem 0'
};

const panelStyle = {
  position: 'fixed',
  backgroundColor: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  zIndex: 5000,
  maxHeight: '200px',
  overflowY: 'auto',
  padding: '0.25rem 0'
};

const optionStyle = {
  padding: '0.55rem 1rem',
  cursor: 'pointer',
  fontSize: '0.875rem',
  color: 'var(--text-color)'
};

/**
 * Parents own selected values and suggestion fetching. completeMethod receives a query request; onChange receives the next value. Enter-to-create is delegated to onKeyPress so a control interaction cannot invent domain records on its own.
 */
function AppAutoComplete({
  value = [], suggestions = [], completeMethod, onChange, onKeyPress,
  field, multiple, placeholder, className, variant, dropdown, autoFocus = false
}) {
  const [query, setQuery] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const getLabel =

  (item) => {
    if (typeof item === 'string') return item;
    if (field && item[field]) return item[field];
    return String(item);
  };

  useEffect(() => {
    if (suggestions.length > 0 && query) {
      setPanelOpen(true);
      setHighlightIndex(0);
    }
  }, [suggestions]);

  useEffect(() => {
    if (!autoFocus) return;

    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => cancelAnimationFrame(frame);
  }, [autoFocus]);

  useEffect(

  () => {
    if (!panelOpen) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setPanelPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    const handleClickOutside =

    (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setPanelOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [panelOpen]);

  const handleInputChange =

  (e) => {
    const q = e.target.value;
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (completeMethod) completeMethod({ query: q });
    }, 200);
  };

  const selectSuggestion =

  (item) => {
    if (multiple) {
      onChange({ value: [...value, item] });
    } else {
      onChange({ value: item });
    }
    setQuery('');
    setPanelOpen(false);
    inputRef.current?.focus();
  };

  const removeChip =

  (index) => {
    const next = [...value];
    next.splice(index, 1);
    onChange({ value: next });
  };

  const handleKeyDown =

  (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && suggestions[highlightIndex]) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightIndex]);
    } else if (e.key === 'Escape') {
      setPanelOpen(false);
    } else if (e.key === 'Backspace' && !query && multiple && value.length > 0) {
      removeChip(value.length - 1);
    }
    // Forward onKeyPress for Enter-to-create
    if (onKeyPress) onKeyPress(e);
  };

  const handleDropdownClick =

  () => {
    if (completeMethod) completeMethod({ query: '' });
    setPanelOpen(true);
    inputRef.current?.focus();
  };

  const isBorderless = variant === 'borderless';
  const cStyle = isBorderless ? borderlessContainerStyle : containerStyle;

  return (
    <div style={wrapperStyle} className={className || ''}>
            <div
        ref={containerRef}
        style={cStyle}
        onClick={() => inputRef.current?.focus()}
        onFocus={isBorderless ? () => {
          if (containerRef.current) containerRef.current.style.borderBottom = '1px solid var(--primary-color)';
        } : undefined}
        onBlur={isBorderless ? () => {
          if (containerRef.current && !panelOpen) containerRef.current.style.borderBottom = '1px solid transparent';
        } : undefined}>

                {multiple && Array.isArray(value) && value.map((item, i) =>
        <span key={i} style={chipStyle}>
                        {getLabel(item)}
                        <button
            type="button"
            style={chipRemoveStyle}
            onClick={(e) => {e.stopPropagation();removeChip(i);}}
            aria-label="Remove">

                            <AppIcon icon="pi pi-times" />
                        </button>
                    </span>
        )}
                <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={!multiple || !value || value.length === 0 ? placeholder : ''}
          style={inputInternalStyle}
          onFocus={() => {
            if (dropdown && completeMethod) completeMethod({ query: '' });
          }} />

                {dropdown &&
        <button
          type="button"
          onClick={handleDropdownClick}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '0 0.25rem',
            color: 'var(--text-color-secondary, #64748b)', fontSize: '0.75rem'
          }}>

                        <AppIcon icon="pi pi-chevron-down" />
                    </button>}

            </div>

            {panelOpen && suggestions.length > 0 && typeof document !== 'undefined' && createPortal(
        <div ref={panelRef} style={{ ...panelStyle, top: panelPos.top, left: panelPos.left, minWidth: panelPos.width }}>
                    {suggestions.map((item, i) =>
          <div
            key={i}
            style={{
              ...optionStyle,
              backgroundColor: i === highlightIndex ? 'var(--surface-hover, var(--surface-ground))' : 'transparent'
            }}
            onMouseEnter={() => setHighlightIndex(i)}
            onClick={() => selectSuggestion(item)}>

                            {getLabel(item)}
                        </div>
          )}
                </div>,
        document.body
      )}
        </div>);

}

export default AppAutoComplete;
